import loggerHelper from '../loggerHelper';
import cardDetailsHelper from '../cardDetailsHelper';

let ctx = {};
let config = {};
let callbacks = {};
let buttonRef = null;
let googlePaymentsClient = null;

const baseRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
};

const getBaseCardPaymentMethod = () => {
  const parameters = {
    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
  };

  if (ctx.globalUserOptions.populateBillingFieldsWithGooglePay) {
    parameters.billingAddressRequired = true;
    parameters.billingAddressParameters = {
      format: 'FULL',
      phoneNumberRequired: true,
    };
  }

  return { type: 'CARD', parameters };
};

const getCardPaymentMethod = () => ({
  tokenizationSpecification: {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: 'adyen',
      gatewayMerchantId: config.gatewayMerchantId,
    },
  },
  ...getBaseCardPaymentMethod(),
});

const getGoogleIsReadyToPayRequest = () => ({
  ...baseRequest,
  allowedPaymentMethods: [getBaseCardPaymentMethod()],
});

const getGoogleTransactionInfo = () => (
  {
    countryCode: ctx.globalUserOptions.googlePayCountryCode,
    currencyCode: ctx.globalUserOptions.googlePayCurrency,
    totalPriceStatus: 'FINAL',
    totalPrice: ctx.globalUserOptions.googlePayAmount
  }
);

const getGooglePaymentDataRequest = () => ({
  ...baseRequest,
  allowedPaymentMethods: [getCardPaymentMethod()],
  transactionInfo: getGoogleTransactionInfo(),
  merchantInfo: {
    merchantId: config.merchantId,
    merchantName: ctx.globalUserOptions.googlePayLabel,
  },
});

const loadSDK = () => new Promise((resolve, reject) => {
  const script = document.createElement('script');
  script.addEventListener('load', resolve, { once: true });
  script.addEventListener('error', reject);
  script.src = config.google_pay_sdk_src;
  script.async = true;
  document.head.appendChild(script);
});

const getGooglePaymentsClient = () => {
  if (googlePaymentsClient === null) {
    googlePaymentsClient = new window.google.payments.api.PaymentsClient({ environment: config.environment });
  }
  return googlePaymentsClient;
}

const addGooglePayButton = () => {
  const paymentsClient = getGooglePaymentsClient();
  buttonRef =
    paymentsClient.createButton({
      buttonColor: ctx.globalUserOptions.googlePayButtonColor || 'default',
      buttonType: ctx.globalUserOptions.googlePayButtonType || 'pay',
      buttonRadius: ctx.globalUserOptions.googlePayButtonRadius || 4,
      buttonBorderType: ctx.globalUserOptions.googlePayButtonBorderType || 'default_border',
      buttonSizeMode: ctx.globalUserOptions.googlePayButtonSizeMode || 'static',
      onClick: onGooglePaymentButtonClicked,
      allowedPaymentMethods: [getBaseCardPaymentMethod()]
    });
  document.querySelector(ctx.globalUserOptions.selectorForGooglePayButton).appendChild(buttonRef);
}

const onGooglePaymentButtonClicked = (e) => {
  e.preventDefault();
  const paymentDataRequest = getGooglePaymentDataRequest();
  const paymentsClient = getGooglePaymentsClient();
  paymentsClient.loadPaymentData(paymentDataRequest)
    .then((paymentData) => {
      processPayment(paymentData);
      hideGooglePayButton();
    })
    .catch((err) => {
      loggerHelper.error(err);
      callbacks.onGooglePayError(err);
    });
}

const processPayment = ({ paymentMethodData }) => {
  const { cardFundingSource, cardNetwork } = paymentMethodData.info || {};
  callbacks.onCardDetailsChange(cardDetailsHelper.fetchCardDetails(cardFundingSource, cardNetwork));
  populateBillingInformation(paymentMethodData.info?.billingAddress);
  ctx.sendMessageToMainIframe({
    action: 'GOOGLE_PAY_TOKEN_RECEIVED',
    data: paymentMethodData.tokenizationData.token,
  });
}

const hideGooglePayButton = () => {
  if (!buttonRef) return;

  buttonRef.style.display = 'none';
};

const populateBillingInformation = (billingAddress) => {
  if (!ctx.globalUserOptions.populateBillingFieldsWithGooglePay) return;
  if (!billingAddress) return;

  const nameParts = billingAddress.name?.trim().split(/\s+/) ?? [];
  const lastName = nameParts.pop() ?? '';
  const firstName = nameParts.join(' ');

  const data = {
    firstName,
    lastName,
    address: billingAddress.address1,
    address2: billingAddress.address2,
    city: billingAddress.locality,
    state: billingAddress.administrativeArea,
    zip: billingAddress.postalCode,
    country: billingAddress.countryCode,
    phone: billingAddress.phoneNumber,
  };

  ctx.sendMessageToAllFields({ action: 'POPULATE_BILLING_FIELDS', data });
  ctx.sendMessageToMainIframe({ action: 'GOOGLE_PAY_BILLING_INFO_RECEIVED', data });
}

const onGooglePayLoaded = () => {
  const paymentsClient = getGooglePaymentsClient();

  paymentsClient.isReadyToPay(getGoogleIsReadyToPayRequest())
    .then((response) => {
      if (response.result) {
        addGooglePayButton();
      }
    })
    .catch((err) => {
      loggerHelper.error(err);
      callbacks.onGooglePayError(err);
    });
};

const init = ({
  sendMessageToMainIframe,
  sendMessageToAllFields,
  createIframes,
  globalUserOptions,
  globalCallbacks,
}) => {
  const {
    selectorForGooglePayButton,
    googlePayAmount,
    googlePayCurrency,
    googlePayCountryCode,
    googlePayLabel,
    googlePayButtonColor,
    googlePayButtonType,
    googlePayButtonRadius,
    googlePayButtonBorderType,
    googlePayButtonSizeMode,
    populateBillingFieldsWithGooglePay,
  } = globalUserOptions;

  ctx = {
    sendMessageToMainIframe,
    sendMessageToAllFields,
    createIframes,
    globalUserOptions: {
      selectorForGooglePayButton,
      googlePayAmount,
      googlePayCurrency,
      googlePayCountryCode,
      googlePayLabel,
      googlePayButtonColor,
      googlePayButtonType,
      googlePayButtonRadius,
      googlePayButtonBorderType,
      googlePayButtonSizeMode,
      populateBillingFieldsWithGooglePay,
    }
  };

  callbacks = globalCallbacks;
};

const fetchConfiguration = () => {
  ctx.sendMessageToMainIframe({ action: 'FETCH_GOOGLE_PAY_CONFIGURATION' });
}

const onConfigurationReceived = async (data) => {
  config = data;
  await ctx.createIframes();

  try {
    await loadSDK();
    onGooglePayLoaded();
  } catch (err) {
    console.error('Failed to load Google Pay JS SDK', err);
  }
};

export default {
  init,
  fetchConfiguration,
  onConfigurationReceived,
};
